import React, { createContext, useContext, useState, useCallback, useEffect } from "react";

const CodeGenerationContext = createContext();

function useProvideCodeGeneration() {
    const [isGeneratingCode, setIsGeneratingCode] = useState(false);
    const [currentWorkflowId, setCurrentWorkflowId] = useState(null);
    const [taskId, setTaskId] = useState(null);
    const [taskStatus, setTaskStatus] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [generationStartTime, setGenerationStartTime] = useState(null);
    const [latestFeedback, setLatestFeedback] = useState(null);

    // beforeunload guard: warn user when closing/refreshing tab during generation
    useEffect(() => {
        if (!isGeneratingCode) return;
        const handleBeforeUnload = (e) => {
            e.preventDefault();
            e.returnValue = "";
        };
        window.addEventListener("beforeunload", handleBeforeUnload);
        return () => window.removeEventListener("beforeunload", handleBeforeUnload);
    }, [isGeneratingCode]);

    const startCodeGeneration = useCallback((workflowId, taskId) => {
        setIsGeneratingCode(true);
        setCurrentWorkflowId(workflowId);
        setTaskId(taskId);
        setTaskStatus("PENDING");
        setGenerationStartTime(Date.now());
        setLatestFeedback(null);
    }, []);

    const updateTaskStatus = useCallback((status, feedback) => {
        setTaskStatus(status);
        if (feedback !== undefined) {
            setLatestFeedback(feedback);
        }
        // Don't set isGeneratingCode to false here — let completeCodeGeneration
        // handle the full cleanup to avoid race conditions with WorkflowGenerationContext
    }, []);

    const completeCodeGeneration = useCallback(() => {
        setIsGeneratingCode(false);
        setTaskId(null);
        setTaskStatus("COMPLETED");
        setGenerationStartTime(null);
        setLatestFeedback(null);
    }, []);

    const cancelCodeGeneration = useCallback(() => {
        setIsGeneratingCode(false);
        setTaskStatus("CANCELLED");
        setShowModal(false);
    }, []);

    const showGenerationModal = useCallback(() => {
        if (isGeneratingCode) {
            setShowModal(true);
        }
    }, [isGeneratingCode]);

    const hideGenerationModal = useCallback(() => {
        setShowModal(false);
    }, []);

    return {
        isGeneratingCode,
        currentWorkflowId,
        taskId,
        taskStatus,
        showModal,
        generationStartTime,
        latestFeedback,
        startCodeGeneration,
        updateTaskStatus,
        completeCodeGeneration,
        cancelCodeGeneration,
        showGenerationModal,
        hideGenerationModal,
    };
}

export function ProvideCodeGeneration({ children }) {
    const codeGeneration = useProvideCodeGeneration();
    return (
        <CodeGenerationContext.Provider value={codeGeneration}>
            {children}
        </CodeGenerationContext.Provider>
    );
}

export function useCodeGeneration() {
    const context = useContext(CodeGenerationContext);
    if (!context) {
        throw new Error(
            "useCodeGeneration must be used within a ProvideCodeGeneration"
        );
    }
    return context;
}
